import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  TextField as MuiTextField,
  InputAdornment,
  Badge,
} from '@mui/material';
import {
  CheckCircle,
  ExpandMore,
  Save,
  Engineering,
  RadioButtonUnchecked,
  Search,
  UnfoldMore,
  UnfoldLess,
  Warning,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { maintenanceService } from '../../services/maintenanceService';
import { StandardTaskAssignment } from '../../types/maintenance';
import { TaskDataField } from '../../types/index';
import { sitesService } from '../../services/sitesService';
import DynamicDataFieldInput from './DynamicDataFieldInput';

interface MaintenanceTaskData {
  id: string;
  task_name: string;
  task_description: string;
  site_name: string;
  site_code: string;
  next_due_date: string;
  days_until_due: number;
  is_overdue: boolean;
  frequency_display: string;
  priority: string;
  priority_display: string;
  status: string;
  status_display: string;
  
  // Assignment tracking
  total_assignments_count: number;
  due_assignments_count: number;
  has_overdue_assignments: boolean;
  overdue_assignments_count: number;
  is_partially_overdue: boolean;
  
  // Detailed assignment data
  task_assignments_detail: StandardTaskAssignment[];
  overdue_assignments_detail: StandardTaskAssignment[];
  
  // Resource information
  estimated_total_duration_hours: number;
  required_crew_size: number;
}

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  employee_id: string;
}

interface WorkOrderData {
  id?: string;
  work_order_number?: string;
  title: string;
  assigned_to?: User;
  supervisor?: User;
  status: 'planned' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent' | 'critical';
  planned_start_date: string;
  planned_end_date: string;
  special_instructions: string;
  safety_notes: string;
}

interface CompletionData {
  work_performed: string;
  findings: string;
  issues_discovered: string;
  parts_replaced: string;
  follow_up_required: boolean;
  follow_up_notes: string;
  completion_status: 'draft' | 'partial' | 'completed';
  data_responses: Record<string, any>;
  completed_assignments: string[];
  work_order?: WorkOrderData;
}

interface EnhancedMaintenanceCompletionFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CompletionData) => void;
  onSaveDraft: (data: CompletionData) => void;
  task: MaintenanceTaskData;
  mode: 'complete' | 'assign' | 'view';
  existingData?: CompletionData;
  availableUsers?: User[];
}

interface AssignmentDataFields {
  assignment: StandardTaskAssignment;
  dataFields: TaskDataField[];
}

interface EquipmentGroup {
  equipment: {
    id: string;
    name: string;
    code: string;
    site_name: string;
  };
  assignments: AssignmentDataFields[];
  totalFields: number;
  completedFields: number;
  isSelected: boolean;
  isExpanded: boolean;
}

const EnhancedMaintenanceCompletionForm: React.FC<EnhancedMaintenanceCompletionFormProps> = ({
  open,
  onClose,
  onSubmit,
  onSaveDraft,
  task,
  mode,
  existingData,
  availableUsers = [],
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CompletionData>({
    work_performed: '',
    findings: '',
    issues_discovered: '',
    parts_replaced: '',
    follow_up_required: false,
    follow_up_notes: '',
    completion_status: 'draft',
    data_responses: {},
    completed_assignments: [],
    work_order: undefined,
    ...existingData,
  });

  const [workOrderData, setWorkOrderData] = useState<WorkOrderData>({
    title: `${task.task_name} - ${task.site_name}`,
    status: 'planned',
    priority: 'normal',
          planned_start_date: dayjs().format('YYYY-MM-DD'),
      planned_end_date: dayjs().format('YYYY-MM-DD'),
    special_instructions: '',
    safety_notes: '',
    ...existingData?.work_order,
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState<string[]>(
    existingData?.completed_assignments || []
  );
  const [assignmentDataFields, setAssignmentDataFields] = useState<AssignmentDataFields[]>([]);
  const [loadingDataFields, setLoadingDataFields] = useState(false);
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'selected' | 'incomplete'>('all');
  const [expandedEquipment, setExpandedEquipment] = useState<Set<string>>(new Set());
  const [equipmentGroups, setEquipmentGroups] = useState<EquipmentGroup[]>([]);

  // Fetch data fields for all assignments (to show available equipment/tasks)
  useEffect(() => {
    const fetchDataFields = async () => {
      setLoadingDataFields(true);
      try {
        const response = await sitesService.getTaskDataFields({ page_size: 100 });
        const allFields = response.results || [];
        
        const assignmentFields: AssignmentDataFields[] = [];
        
        // Get data fields for ALL assignments (not just selected ones)
        task.task_assignments_detail.forEach(assignment => {
          const matchingFields = allFields.filter((field: any) => 
            field.standard_task === assignment.standard_task
          );
          
          // Include assignment even if no data fields (for selection purposes)
          assignmentFields.push({
            assignment,
            dataFields: matchingFields.sort((a, b) => a.display_order - b.display_order)
          });
        });
        
        setAssignmentDataFields(assignmentFields);
      } catch (error) {
        console.error('Error fetching data fields:', error);
        setAssignmentDataFields([]);
      } finally {
        setLoadingDataFields(false);
      }
    };

    fetchDataFields();
  }, [task.task_assignments_detail]);

  // Group assignments by equipment with progress tracking
  useEffect(() => {
    const groups: Record<string, EquipmentGroup> = {};
    
    assignmentDataFields.forEach(({ assignment, dataFields }) => {
      const equipmentId = assignment.equipment.toString();
      
      if (!groups[equipmentId]) {
        groups[equipmentId] = {
          equipment: {
            id: equipmentId,
            name: assignment.equipment_name,
            code: assignment.equipment_code,
            site_name: assignment.site_name,
          },
          assignments: [],
          totalFields: 0,
          completedFields: 0,
          isSelected: selectedAssignments.includes(assignment.id),
          isExpanded: false, // Don't auto-expand equipment sections
        };
      }
      
      groups[equipmentId].assignments.push({ assignment, dataFields });
      groups[equipmentId].totalFields += dataFields.length;
      
      // Count completed fields for this equipment
      dataFields.forEach(field => {
        const fieldKey = getFieldKey(assignment.id, field.id);
        if (formData.data_responses[fieldKey]) {
          groups[equipmentId].completedFields += 1;
        }
      });
      
      // Update selection status
      if (selectedAssignments.includes(assignment.id)) {
        groups[equipmentId].isSelected = true;
      }
    });
    
    setEquipmentGroups(Object.values(groups).sort((a, b) => a.equipment.name.localeCompare(b.equipment.name)));
  }, [assignmentDataFields, selectedAssignments, formData.data_responses]); // Removed expandedEquipment dependency

  // Calculate completion status intelligently
  const getCompletionStatus = (): 'draft' | 'partial' | 'completed' => {
    if (selectedAssignments.length === 0) return 'draft';
    
    // Check if all selected assignments have completed their required data fields
    let totalRequiredFields = 0;
    let completedRequiredFields = 0;
    
    assignmentDataFields.forEach(({ assignment, dataFields }) => {
      if (selectedAssignments.includes(assignment.id)) {
        const requiredFields = dataFields.filter(field => field.is_required);
        totalRequiredFields += requiredFields.length;
        
        requiredFields.forEach(field => {
          const fieldKey = getFieldKey(assignment.id, field.id);
          const value = formData.data_responses[fieldKey];
          if (value !== undefined && value !== null && value !== '') {
            completedRequiredFields += 1;
          }
        });
      }
    });
    
    // Check if all assignments in the task are selected (complete task scenario)
    const totalAssignmentsInTask = task.task_assignments_detail.length;
    const selectedAssignmentsCount = selectedAssignments.length;
    
    if (selectedAssignmentsCount === totalAssignmentsInTask && 
        (totalRequiredFields === 0 || completedRequiredFields === totalRequiredFields)) {
      return 'completed';
    } else if (completedRequiredFields > 0 || selectedAssignmentsCount > 0) {
      return 'partial';
    }
    
    return 'draft';
  };

  // Handle field changes with assignment context
  const handleFieldChange = (assignmentId: string, fieldId: number, value: any) => {
    const fieldKey = getFieldKey(assignmentId, fieldId);
    setFormData(prev => ({
      ...prev,
      data_responses: {
        ...prev.data_responses,
        [fieldKey]: value,
      },
    }));

    // Clear validation error for this field
    const errorKey = `data_${fieldKey}`;
    if (validationErrors[errorKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Group fields by section for each assignment
  const getGroupedFieldsForAssignment = (dataFields: TaskDataField[]) => {
    return dataFields.reduce((acc, field) => {
      const section = field.section_name || 'General';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(field);
      return acc;
    }, {} as Record<string, TaskDataField[]>);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (activeStep === 1) {
      // Assignment selection validation
      if (selectedAssignments.length === 0) {
        errors.assignments = 'Please select at least one assignment to complete';
      }
    }
    
    if (activeStep === 2) {
      // Data fields validation per assignment
      assignmentDataFields.forEach(({ assignment, dataFields }) => {
        if (selectedAssignments.includes(assignment.id)) {
          dataFields.forEach(field => {
            if (field.is_required) {
              const fieldKey = getFieldKey(assignment.id, field.id);
              const value = formData.data_responses[fieldKey];
              if (!value && value !== 0) {
                errors[`data_${fieldKey}`] = `${field.field_label} is required for ${assignment.equipment_name}`;
              }
            }
            
            // Numeric validation
            if (field.is_numeric && formData.data_responses[getFieldKey(assignment.id, field.id)]) {
              const numValue = parseFloat(formData.data_responses[getFieldKey(assignment.id, field.id)]);
              if (field.min_value !== null && numValue < field.min_value) {
                errors[`data_${getFieldKey(assignment.id, field.id)}`] = `Value must be at least ${field.min_value}`;
              }
              if (field.max_value !== null && numValue > field.max_value) {
                errors[`data_${getFieldKey(assignment.id, field.id)}`] = `Value must be no more than ${field.max_value}`;
              }
            }
          });
        }
      });
    }
    
    if (activeStep === 3) {
      // Completion validation
      if (!formData.work_performed.trim()) {
        errors.work_performed = 'Please describe the work performed';
      }
      
      // Add warnings for incomplete tasks
      const completionStatus = getCompletionStatus();
      if (completionStatus === 'partial') {
        const totalTasks = task.task_assignments_detail.length;
        const selectedTasks = selectedAssignments.length;
        if (selectedTasks < totalTasks) {
          errors.incompleteWarning = `Warning: Only ${selectedTasks} of ${totalTasks} tasks selected. This will be marked as partial completion.`;
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).filter(key => key !== 'incompleteWarning').length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      const draftData = {
        ...formData,
        completion_status: 'draft' as const,
        completed_assignments: selectedAssignments,
        work_order: activeStep >= 0 ? workOrderData : undefined,
      };
      await onSaveDraft(draftData);
    } finally {
      setLoading(false);
    }
  };

  // Early completion validation
  const checkEarlyCompletionAllowed = (task: MaintenanceTaskData): { allowed: boolean; message?: string; daysUntil?: number } => {
    if (!task.next_due_date) {
      return { allowed: true };
    }

    const today = new Date();
    const dueDate = new Date(task.next_due_date);
    const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    // If already overdue or due, allow completion
    if (daysUntilDue <= 0) {
      return { allowed: true };
    }

    // Check if any assignments have advance notice settings
    const assignments = task.task_assignments_detail || [];
    if (assignments.length === 0) {
      return { allowed: true };
    }

    // Get the minimum advance notice from all assignments (most restrictive)
    const minAdvanceNotice = Math.min(...assignments.map(a => a.advance_notice_days || 7));
    const daysUntilEarliestCompletion = daysUntilDue - minAdvanceNotice;

    if (daysUntilEarliestCompletion > 0) {
      return {
        allowed: false,
        message: `This task cannot be completed yet. Earliest completion date is ${minAdvanceNotice} days before due date.`,
        daysUntil: daysUntilEarliestCompletion
      };
    }

    return { allowed: true };
  };

  const earlyCompletionCheck = checkEarlyCompletionAllowed(task);

  const handleSubmit = async () => {
    // Check early completion validation
    if (!earlyCompletionCheck.allowed) {
      alert(`${earlyCompletionCheck.message}\n\nYou must wait ${earlyCompletionCheck.daysUntil} more days before completing this task.`);
      return;
    }

    try {
      setLoading(true);
      setValidationErrors({});

      // Validate required fields
      const errors: Record<string, string> = {};
      if (!formData.work_performed.trim()) {
        errors.work_performed = 'Work performed description is required';
      }

      // Validate selected assignments
      if (selectedAssignments.length === 0) {
        errors.assignments = 'At least one assignment must be selected';
      }

      // Validate data fields for selected assignments
      selectedAssignments.forEach(assignmentId => {
        const assignmentFields = assignmentDataFields.find(
          item => item.assignment.id === assignmentId
        );
        if (assignmentFields) {
          assignmentFields.dataFields.forEach(field => {
            if (field.is_required) {
              const fieldKey = getFieldKey(assignmentId, field.id);
              if (!formData.data_responses[fieldKey]) {
                errors[`data_${fieldKey}`] = `${field.field_label} is required`;
              }
            }
          });
        }
      });

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Determine completion status based on task selection and data completion
      const allTasksSelected = selectedAssignments.length === task.task_assignments_detail.length;
      const totalDataFields = assignmentDataFields.reduce((sum, item) => {
        return selectedAssignments.includes(item.assignment.id) ? sum + item.dataFields.length : sum;
      }, 0);
      const completedDataFields = Object.keys(formData.data_responses).filter(key => 
        formData.data_responses[key] && formData.data_responses[key].toString().trim() !== ''
      ).length;

      let autoStatus: 'completed' | 'partial' | 'draft' = 'draft';
      if (allTasksSelected && completedDataFields === totalDataFields) {
        autoStatus = 'completed';
      } else if (selectedAssignments.length > 0 && completedDataFields > 0) {
        autoStatus = 'partial';
      }

      const submitData = {
        ...formData,
        completion_status: autoStatus,
        completed_assignments: selectedAssignments,
        work_order: activeStep >= 0 ? workOrderData : undefined,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Failed to submit form:', error);
      alert('Failed to submit completion data');
    } finally {
      setLoading(false);
    }
  };

  // Create unique key for assignment + field combination
  const getFieldKey = (assignmentId: string, fieldId: number) => `${assignmentId}_${fieldId}`;

  // Handle equipment selection (select all assignments for an equipment)
  const handleEquipmentToggle = (equipmentId: string, isSelected: boolean) => {
    const group = equipmentGroups.find(g => g.equipment.id === equipmentId);
    if (!group) return;
    
    const equipmentAssignmentIds = group.assignments.map(a => a.assignment.id);
    
    if (isSelected) {
      setSelectedAssignments(prev => {
        const combined = [...prev, ...equipmentAssignmentIds];
        return Array.from(new Set(combined));
      });
    } else {
      setSelectedAssignments(prev => prev.filter(id => !equipmentAssignmentIds.includes(id)));
    }
  };

  // Handle expand/collapse equipment
  const handleEquipmentExpand = (equipmentId: string) => {
    setExpandedEquipment(prev => {
      const newSet = new Set(prev);
      if (newSet.has(equipmentId)) {
        newSet.delete(equipmentId);
      } else {
        newSet.add(equipmentId);
      }
      return newSet;
    });
  };

  // Filter equipment based on search and view mode
  const filteredEquipmentGroups = equipmentGroups.filter(group => {
    // Text filter
    if (equipmentFilter) {
      const searchLower = equipmentFilter.toLowerCase();
      const matchesName = group.equipment.name.toLowerCase().includes(searchLower);
      const matchesCode = group.equipment.code.toLowerCase().includes(searchLower);
      const matchesSite = group.equipment.site_name.toLowerCase().includes(searchLower);
      if (!matchesName && !matchesCode && !matchesSite) return false;
    }
    
    // View mode filter
    switch (viewMode) {
      case 'selected':
        return group.isSelected;
      case 'incomplete':
        return group.completedFields < group.totalFields;
      default:
        return true;
    }
  });

  // Calculate overall progress
  const totalFields = equipmentGroups.reduce((sum, group) => sum + group.totalFields, 0);
  const completedFields = equipmentGroups.reduce((sum, group) => sum + group.completedFields, 0);
  const overallProgress = totalFields > 0 ? (completedFields / totalFields) * 100 : 0;

  // Bulk actions
  const handleSelectAll = () => {
    const allAssignmentIds = filteredEquipmentGroups.flatMap(group => 
      group.assignments.map(a => a.assignment.id)
    );
    setSelectedAssignments(allAssignmentIds);
  };

  const handleDeselectAll = () => {
    setSelectedAssignments([]);
  };

  const handleExpandAll = () => {
    const allEquipmentIds = filteredEquipmentGroups.map(group => group.equipment.id);
    setExpandedEquipment(new Set(allEquipmentIds));
  };

  const handleCollapseAll = () => {
    setExpandedEquipment(new Set());
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0: // Work Order Assignment
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Create Work Order
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Assign this maintenance task to a technician and create a work order for tracking.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Work Order Title"
                  value={workOrderData.title}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, title: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={availableUsers}
                  getOptionLabel={(option) => `${option.full_name} (${option.employee_id})`}
                  value={workOrderData.assigned_to || null}
                  onChange={(_, newValue) => setWorkOrderData(prev => ({ ...prev, assigned_to: newValue || undefined }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign to Technician"
                      placeholder="Search users..."
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={availableUsers}
                  getOptionLabel={(option) => `${option.full_name} (${option.employee_id})`}
                  value={workOrderData.supervisor || null}
                  onChange={(_, newValue) => setWorkOrderData(prev => ({ ...prev, supervisor: newValue || undefined }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supervisor"
                      placeholder="Search supervisors..."
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={workOrderData.priority}
                    onChange={(e) => setWorkOrderData(prev => ({ ...prev, priority: e.target.value as any }))}
                    label="Priority"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={workOrderData.status}
                    onChange={(e) => setWorkOrderData(prev => ({ ...prev, status: e.target.value as any }))}
                    label="Status"
                  >
                    <MenuItem value="planned">Planned</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Planned Start Date"
                  value={workOrderData.planned_start_date}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, planned_start_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Planned End Date"
                  value={workOrderData.planned_end_date}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, planned_end_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Special Instructions"
                  value={workOrderData.special_instructions}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, special_instructions: e.target.value }))}
                  placeholder="Any special instructions for this work..."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Safety Notes"
                  value={workOrderData.safety_notes}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, safety_notes: e.target.value }))}
                  placeholder="Important safety considerations..."
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Assignment Selection - Enhanced with Equipment Grouping
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Equipment & Assignments
            </Typography>
            
            {/* Progress Summary */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'primary.light', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'primary.contrastText' }}>
                  Overall Progress: {completedFields} / {totalFields} fields
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.contrastText' }}>
                  {Math.round(overallProgress)}% Complete
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={overallProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'success.main'
                  }
                }}
              />
            </Box>

            {/* Controls */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Search */}
              <MuiTextField
                size="small"
                placeholder="Search equipment..."
                value={equipmentFilter}
                onChange={(e) => setEquipmentFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              
              {/* View Mode Toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="all">All ({equipmentGroups.length})</ToggleButton>
                <ToggleButton value="selected">Selected ({equipmentGroups.filter(g => g.isSelected).length})</ToggleButton>
                <ToggleButton value="incomplete">Incomplete ({equipmentGroups.filter(g => g.completedFields < g.totalFields).length})</ToggleButton>
              </ToggleButtonGroup>
              
              {/* Bulk Actions */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={handleSelectAll} startIcon={<CheckCircle />}>
                  Select All
                </Button>
                <Button size="small" onClick={handleDeselectAll} startIcon={<RadioButtonUnchecked />}>
                  Deselect All
                </Button>
                <Button size="small" onClick={handleExpandAll} startIcon={<UnfoldMore />}>
                  Expand All
                </Button>
                <Button size="small" onClick={handleCollapseAll} startIcon={<UnfoldLess />}>
                  Collapse All
                </Button>
              </Box>
            </Box>

            {/* Equipment Groups */}
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {filteredEquipmentGroups.map((group) => (
                <Accordion
                  key={group.equipment.id}
                  expanded={expandedEquipment.has(group.equipment.id)}
                  onChange={() => handleEquipmentExpand(group.equipment.id)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    sx={{ 
                      backgroundColor: group.isSelected ? 'action.selected' : 'background.default',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                      <Checkbox
                        checked={group.isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleEquipmentToggle(group.equipment.id, e.target.checked);
                        }}
                        color="primary"
                      />
                      
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {group.equipment.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip size="small" label={`Code: ${group.equipment.code}`} />
                          <Chip size="small" label={`Site: ${group.equipment.site_name}`} />
                          <Chip 
                            size="small" 
                            label={`${group.assignments.length} tasks`}
                            color="primary"
                          />
                        </Box>
                      </Box>
                      
                      <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                        <Badge 
                          badgeContent={group.completedFields} 
                          max={999}
                          color={group.completedFields === group.totalFields ? 'success' : 'primary'}
                          sx={{ mr: 1 }}
                        >
                          <Typography variant="body2">
                            {group.totalFields} fields
                          </Typography>
                        </Badge>
                        <LinearProgress
                          variant="determinate"
                          value={group.totalFields > 0 ? (group.completedFields / group.totalFields) * 100 : 0}
                          sx={{ width: 100, mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Tasks for {group.equipment.name}:
                    </Typography>
                    
                    {group.assignments.map(({ assignment, dataFields }) => (
                      <Box key={assignment.id} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Checkbox
                            checked={selectedAssignments.includes(assignment.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAssignments(prev => [...prev, assignment.id]);
                              } else {
                                setSelectedAssignments(prev => prev.filter(id => id !== assignment.id));
                              }
                            }}
                          />
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">
                              {assignment.task_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {assignment.effective_description}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Chip 
                                size="small" 
                                label={`${dataFields.length} data fields`} 
                                variant="outlined"
                              />
                              {assignment.is_overdue && (
                                <Chip 
                                  size="small" 
                                  label="OVERDUE" 
                                  color="error" 
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {selectedAssignments.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Select equipment and assignments to proceed with data collection.
              </Alert>
            )}
          </Box>
        );

      case 2: // Data Collection
        // Group assignments by equipment for compact display
        const equipmentDataGroups = assignmentDataFields.reduce((groups, { assignment, dataFields }) => {
          const equipmentKey = assignment.equipment.toString();
          
          if (!groups[equipmentKey]) {
            groups[equipmentKey] = {
              equipment: {
                id: equipmentKey,
                name: assignment.equipment_name,
                code: assignment.equipment_code,
              },
              assignments: []
            };
          }
          
          groups[equipmentKey].assignments.push({ assignment, dataFields });
          return groups;
        }, {} as Record<string, { equipment: { id: string; name: string; code: string; }; assignments: AssignmentDataFields[] }>);

        // Filter to only show selected assignments
        const selectedEquipmentGroups = Object.values(equipmentDataGroups).map(group => ({
          ...group,
          assignments: group.assignments.filter(({ assignment }) => 
            selectedAssignments.includes(assignment.id)
          )
        })).filter(group => group.assignments.length > 0);

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Task Data Collection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete the required data fields for the selected assignments.
            </Typography>

            {loadingDataFields ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading data fields...</Typography>
              </Box>
            ) : selectedEquipmentGroups.length === 0 ? (
              <Alert severity="info">
                No data collection fields are required for the selected assignments.
              </Alert>
            ) : (
              selectedEquipmentGroups.map((group, groupIndex) => (
                <Box key={group.equipment.id} sx={{ mb: groupIndex < selectedEquipmentGroups.length - 1 ? 3 : 0 }}>
                  {/* Simplified Equipment Header */}
                  <Typography variant="h6" sx={{ 
                    mb: 2, 
                    p: 2, 
                    backgroundColor: 'primary.light', 
                    borderRadius: 1,
                    color: 'primary.contrastText',
                    fontWeight: 'bold'
                  }}>
                    {group.equipment.code} - {group.equipment.name}
                  </Typography>

                  {/* All tasks for this equipment */}
                  {group.assignments.map(({ assignment, dataFields }, assignmentIndex) => (
                    <Box key={assignment.id} sx={{ mb: 2 }}>
                      {/* Task name (compact) */}
                      <Typography variant="subtitle1" sx={{ 
                        mb: 1, 
                        color: 'text.secondary',
                        fontWeight: 'medium'
                      }}>
                        {assignment.task_name}
                      </Typography>

                      {/* Data fields for this task */}
                      {dataFields.length > 0 ? (
                        // Group fields by section, but skip "General" unless explicitly named
                        (() => {
                          const groupedFields = getGroupedFieldsForAssignment(dataFields);
                          
                          return Object.entries(groupedFields).map(([sectionName, fields]) => {
                            // Skip rendering section header for default "General"
                            const isDefaultGeneral = sectionName === 'General' && 
                              fields.every(field => !field.section_name || field.section_name === 'General');
                            
                            return (
                              <Box key={`${assignment.id}_${sectionName}`} sx={{ mb: 1 }}>
                                {/* Only show section header if explicitly configured (not default "General") */}
                                {!isDefaultGeneral && (
                                  <Typography variant="subtitle2" sx={{ 
                                    mb: 1, 
                                    color: 'primary.main',
                                    fontWeight: 'medium'
                                  }}>
                                    {sectionName}
                                  </Typography>
                                )}
                                
                                <Grid container spacing={2}>
                                  {fields.map((field) => (
                                    <Grid item xs={12} md={6} key={`${assignment.id}_${field.id}`}>
                                      <DynamicDataFieldInput
                                        field={field}
                                        value={formData.data_responses[getFieldKey(assignment.id, field.id)] || ''}
                                        onChange={(value) => handleFieldChange(assignment.id, field.id, value)}
                                        error={validationErrors[`data_${getFieldKey(assignment.id, field.id)}`]}
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            );
                          });
                        })()
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                          No data collection required for this task.
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        );

      case 3: // Completion Summary
        const completionStatus = getCompletionStatus();
        const totalTasks = task.task_assignments_detail.length;
        const selectedTasks = selectedAssignments.length;
        
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Work Completion Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Provide a summary of the work performed and any findings.
            </Typography>

            {/* Completion Status Alert */}
            {completionStatus === 'partial' && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2">Partial Completion Detected</Typography>
                <Typography variant="body2">
                  {selectedTasks} of {totalTasks} tasks selected. 
                  This maintenance will be marked as <strong>partially complete</strong>.
                  {selectedTasks < totalTasks && (
                    <span> Consider selecting all tasks for full completion.</span>
                  )}
                </Typography>
              </Alert>
            )}

            {completionStatus === 'completed' && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="subtitle2">Ready for Full Completion</Typography>
                <Typography variant="body2">
                  All tasks selected and required data completed. 
                  This maintenance will be marked as <strong>fully complete</strong>.
                </Typography>
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Work Performed *"
                  value={formData.work_performed}
                  onChange={(e) => setFormData(prev => ({ ...prev, work_performed: e.target.value }))}
                  placeholder="Describe the maintenance work that was completed..."
                  error={!!validationErrors.work_performed}
                  helperText={validationErrors.work_performed}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Findings"
                  value={formData.findings}
                  onChange={(e) => setFormData(prev => ({ ...prev, findings: e.target.value }))}
                  placeholder="Any observations or findings during the work..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Issues Discovered"
                  value={formData.issues_discovered}
                  onChange={(e) => setFormData(prev => ({ ...prev, issues_discovered: e.target.value }))}
                  placeholder="Any problems or issues found..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Parts Replaced"
                  value={formData.parts_replaced}
                  onChange={(e) => setFormData(prev => ({ ...prev, parts_replaced: e.target.value }))}
                  placeholder="List any parts that were replaced..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Completion Status (Auto-determined)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {completionStatus === 'completed' ? (
                      <>
                        <CheckCircle color="success" />
                        <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                          Fully Complete
                        </Typography>
                      </>
                    ) : completionStatus === 'partial' ? (
                      <>
                        <Warning color="warning" />
                        <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'warning.main' }}>
                          Partial Completion
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Save color="action" />
                        <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          Draft
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.follow_up_required}
                      onChange={(e) => setFormData(prev => ({ ...prev, follow_up_required: e.target.checked }))}
                    />
                  }
                  label="Follow-up Work Required"
                />
              </Grid>

              {formData.follow_up_required && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Follow-up Notes"
                    value={formData.follow_up_notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, follow_up_notes: e.target.value }))}
                    placeholder="Describe what follow-up work is needed..."
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const steps = mode === 'assign' 
    ? ['Create Work Order', 'Select Assignments', 'Data Collection', 'Completion Summary']
    : ['Select Assignments', 'Data Collection', 'Completion Summary'];

  const adjustedActiveStep = activeStep;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'assign' ? <Engineering color="primary" /> : <CheckCircle color="primary" />}
          {mode === 'assign' ? 'Assign Maintenance Task' : 'Complete Maintenance Task'}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: '80vh', overflow: 'hidden' }}>
        {/* Early Completion Warning */}
        {!earlyCompletionCheck.allowed && (
          <Alert severity="warning" sx={{ m: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              ⚠️ Early Completion Not Allowed
            </Typography>
            <Typography variant="body2">
              {earlyCompletionCheck.message}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              You must wait <strong>{earlyCompletionCheck.daysUntil} more days</strong> before completing this task.
            </Typography>
          </Alert>
        )}
        
        {/* Task is due soon or overdue */}
        {task.days_until_due <= 7 && task.days_until_due > 0 && (
          <Alert severity="info" sx={{ m: 2, mb: 1 }}>
            <Typography variant="body2">
              📅 This task is due in <strong>{task.days_until_due} days</strong> ({task.next_due_date})
            </Typography>
          </Alert>
        )}
        
        {/* Task is overdue */}
        {task.is_overdue && (
          <Alert severity="error" sx={{ m: 2, mb: 1 }}>
            <Typography variant="body2">
              🚨 This task is <strong>{Math.abs(task.days_until_due)} days overdue</strong>
            </Typography>
          </Alert>
        )}

        <Box sx={{ height: '100%', overflow: 'auto' }}>
          <DialogContent>
            {/* Task Summary */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6">{task.task_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Site: {task.site_name} • Priority: {task.priority_display}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {task.task_assignments_detail.length} assignments • Estimated: {task.estimated_total_duration_hours}h
                </Typography>
                {task.is_overdue && (
                  <Chip
                    size="small"
                    label="Overdue"
                    color="error"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>

            {/* Progress Stepper */}
            <Stepper activeStep={adjustedActiveStep} orientation="vertical">
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                  <StepContent>
                    {getStepContent(mode === 'assign' ? index : index + 1)}
                    
                    <Box sx={{ mb: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={index === steps.length - 1 ? handleSubmit : handleNext}
                        sx={{ mr: 1 }}
                        disabled={loading}
                      >
                        {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        {index === steps.length - 1 ? (
                          mode === 'assign' ? 'Create Work Order' : 'Complete Task'
                        ) : 'Next'}
                      </Button>
                      {index > 0 && (
                        <Button onClick={handleBack} sx={{ mr: 1 }}>
                          Back
                        </Button>
                      )}
                      <Button
                        onClick={handleSaveDraft}
                        startIcon={<Save />}
                        disabled={loading}
                      >
                        Save Draft
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </DialogContent>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedMaintenanceCompletionForm; 